/* Customer Request for Quotation — inbound RFQ from customers.
 *
 * Distinct from the supplier-side RFQ Workspace (`/rfqs`).
 * Use case: a customer (or our salesperson on the customer's behalf)
 * captures what the customer wants quoted. Flow:
 *     Customer RFQ  →  BOQ Builder  →  Our Quotation  →  Sales Order
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import {
  Plus, Trash2, X, FileText, Save, Send, Eye,
  Upload, Paperclip, Download,
} from 'lucide-react';

const STATUS_TONE = {
  draft:     'bg-gray-100 text-gray-700',
  saved:     'bg-emerald-100 text-emerald-900',
  converted: 'bg-violet-100 text-violet-900',
  cancelled: 'bg-rose-100 text-rose-800',
};

const BLANK_LINE = () => ({
  item_ref: '', category: '', type_of_work: '', description: '',
  size: '', unit: '', qty: 1,
  requested_brand: '', brand_strictness: '',   // Specified | Equivalent | Open
});


// ──────────────────────── List Page ─────────────────────────
const CustomerRFQ = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // for "edit draft"
  const [viewing, setViewing] = useState(null);   // for detail modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/customer-rfqs?limit=300');
      setRows(r.data?.rfqs || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (rfq) => {
    try {
      const r = await axiosInstance.get(
        `/customer-rfqs/${rfq.id}/pdf`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${rfq.rfq_no}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error('PDF generation failed');
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="customer-rfq-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold inline-flex items-center gap-2">
          <FileText className="w-6 h-6" /> Request for Quotation
          <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-900 text-white"
                data-testid="customer-rfq-count">{rows.length}</span>
        </h1>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
                data-testid="customer-rfq-new-button"
                className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add New Request
        </button>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">RFQ No.</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Project</th>
              <th className="text-left px-3 py-2">Contact Person</th>
              <th className="text-right px-3 py-2">Lines</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center text-gray-400 py-8">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-gray-400 py-8" data-testid="customer-rfq-empty">
                No requests yet — click <strong>Add New Request</strong>.
              </td></tr>
            )}
            {rows.map(r => {
              // Visual pricing pipeline: every saved RFQ enters as
              // `unpriced` (red row) → flips to `priced` (green row)
              // when at least one Vendor Quotation is linked to it via
              // the Vendor Quotations page. Drafts stay neutral.
              const isUnpriced = r.status === 'saved' && r.pricing_status !== 'priced';
              const isPriced   = r.pricing_status === 'priced';
              const rowTone = isUnpriced
                ? 'bg-red-50 hover:bg-red-100 border-l-4 border-red-400'
                : isPriced
                  ? 'bg-emerald-50 hover:bg-emerald-100 border-l-4 border-emerald-400'
                  : 'hover:bg-indigo-50/30';
              return (
              <tr key={r.id}
                  onClick={() => setViewing(r)}
                  data-testid={`customer-rfq-row-${r.rfq_no}`}
                  className={`border-t border-gray-100 cursor-pointer ${rowTone}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.rfq_no}</td>
                <td className="px-3 py-2 text-xs">{r.rfq_date}</td>
                <td className="px-3 py-2">{r.customer_name || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.project_name || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.contact_person || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{(r.lines || []).length}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[r.status] || STATUS_TONE.draft}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={(e) => { e.stopPropagation(); setViewing(r); }}
                          data-testid={`customer-rfq-view-${r.rfq_no}`}
                          className="text-xs text-indigo-700 hover:underline mr-2">
                    <Eye className="w-3 h-3 inline" /> view
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); downloadPdf(r); }}
                          data-testid={`customer-rfq-pdf-${r.rfq_no}`}
                          className="text-xs text-rose-700 hover:underline mr-2 inline-flex items-center gap-0.5">
                    <Download className="w-3 h-3" /> PDF
                  </button>
                  {/* Edit button — visible on EVERY row (drafts AND saved)
                      so the author can correct typos / add lines even
                      after submission. Backend enforces the same
                      ownership rule: only the creator or admin can save
                      a change. */}
                  <button onClick={(e) => { e.stopPropagation(); setEditing(r); setShowForm(true); }}
                          data-testid={`customer-rfq-edit-${r.rfq_no}`}
                          className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold border border-indigo-200">
                    ✎ Edit
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <CustomerRFQForm
          existing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {viewing && (
        <CustomerRFQDetail rfq={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
};


// ─────────────────────── Add/Edit Form Modal ─────────────────
const CustomerRFQForm = ({ existing, onClose, onSaved }) => {
  const [meta, setMeta] = useState({ categories: [], types_of_work: [], units: [] });
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  // Pending uploads (for New RFQ — not yet saved): {boq: File, specs: File, drawing: File}
  const [pendingFiles, setPendingFiles] = useState({ boq: null, specs: null, drawing: null });
  // Existing server-side attachments (for Edit Draft mode).
  const [existingAttachments, setExistingAttachments] = useState(
    existing?.attachments || []
  );
  const [form, setForm] = useState(() => existing ? {
    rfq_date:       existing.rfq_date,
    customer_id:    existing.customer_id || '',
    project_id:     existing.project_id || '',
    contact_person: existing.contact_person || '',
    notes:          existing.notes || '',
    lines:          (existing.lines || []).map(({no, ...rest}) => rest),
  } : {
    rfq_date:       new Date().toISOString().slice(0, 10),
    customer_id:    '',
    project_id:     '',
    contact_person: '',
    notes:          '',
    lines:          [BLANK_LINE()],
  });

  useEffect(() => {
    Promise.all([
      axiosInstance.get('/customer-rfqs/meta').catch(() => ({data: {}})),
      axiosInstance.get('/customers?limit=500').catch(() => ({data: {customers: []}})),
      axiosInstance.get('/projects?limit=500').catch(() => ({data: []})),
    ]).then(([m, c, p]) => {
      setMeta(m.data || {});
      setCustomers(c.data?.customers || (Array.isArray(c.data) ? c.data : []));
      setProjects(Array.isArray(p.data) ? p.data : (p.data?.projects || []));
    });
  }, []);

  const updateLine = (idx, key, val) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [key]: val };
      // Auto-generate Item Ref when a category is picked. Pattern:
      // {CATEGORY_CODE}-{sequence among lines of the same category}.
      // We only auto-fill when the field is empty so a manual override
      // is never clobbered.
      if (key === 'category' && val) {
        const sameCategoryCount = lines
          .slice(0, idx + 1)
          .filter((l) => l.category === val).length;
        if (!lines[idx].item_ref || /^[A-Z]{2}-\d{3}$/.test(lines[idx].item_ref || '')) {
          lines[idx].item_ref = `${val}-${String(sameCategoryCount).padStart(3, '0')}`;
        }
      }
      return { ...f, lines };
    });
  };
  const addLine  = () => setForm(f => ({ ...f, lines: [...f.lines, BLANK_LINE()] }));
  const dropLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const uploadFile = async (rfqId, kind, file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    try {
      const r = await axiosInstance.post(
        `/customer-rfqs/${rfqId}/attachments`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return r.data;
    } catch (e) {
      const err = e?.response?.data?.detail;
      toast.error(`Upload ${kind} failed: ${err?.error || 'error'}`);
      return null;
    }
  };

  const uploadPending = async (rfqId) => {
    const kinds = ['boq', 'specs', 'drawing'];
    const todo = kinds.filter(k => pendingFiles[k]);
    if (todo.length === 0) return;
    await Promise.all(todo.map(k => uploadFile(rfqId, k, pendingFiles[k])));
    toast.success(`${todo.length} file(s) uploaded`);
  };

  const onEditUpload = async (kind, file) => {
    if (!existing || !file) return;
    const att = await uploadFile(existing.id, kind, file);
    if (att) {
      setExistingAttachments(prev => [...prev, att]);
      toast.success(`${kind} attached`);
    }
  };

  const removeExisting = async (attachmentId) => {
    if (!existing) return;
    try {
      await axiosInstance.delete(`/customer-rfqs/${existing.id}/attachments/${attachmentId}`);
      setExistingAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (_) { toast.error('Delete failed'); }
  };

  const submit = async (statusToSet) => {
    // Save (final) requires at least one line with description + qty.
    if (statusToSet === 'saved') {
      const valid = (form.lines || []).filter(l => (l.description || '').trim() && Number(l.qty || 0) > 0);
      if (valid.length === 0) {
        toast.error('Add at least one line with description and qty to Save');
        return;
      }
    }
    setBusy(true);
    try {
      const payload = { ...form, status: statusToSet };
      let rfqId = existing?.id;
      if (existing) {
        await axiosInstance.patch(`/customer-rfqs/${existing.id}`, payload);
        toast.success(`Updated (${statusToSet})`);
      } else {
        const r = await axiosInstance.post('/customer-rfqs', payload);
        rfqId = r.data?.id;
        toast.success(`${r.data?.rfq_no} ${statusToSet === 'saved' ? 'saved' : 'drafted'}`);
      }
      if (rfqId) await uploadPending(rfqId);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto"
         data-testid="customer-rfq-form-modal">
      <div className="bg-white w-full min-h-screen">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <h2 className="text-xl font-bold inline-flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {existing ? `Edit Draft · ${existing.rfq_no}` : 'New Request for Quotation'}
          </h2>
          <button onClick={onClose} data-testid="customer-rfq-form-close"
                  className="text-gray-400 hover:text-gray-700 text-3xl leading-none px-2">×</button>
        </div>

        <div className="p-6 space-y-4 max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div>
              <label className="text-xs font-semibold text-gray-700">Date</label>
              <input type="date" value={form.rfq_date}
                      onChange={(e) => setForm(f => ({...f, rfq_date: e.target.value}))}
                      data-testid="customer-rfq-form-date"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">
                Customer <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select value={form.customer_id}
                       onChange={(e) => setForm(f => ({...f, customer_id: e.target.value}))}
                       data-testid="customer-rfq-form-customer"
                       className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white">
                <option value="">— none —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name_english || c.name_arabic || c.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">
                Project <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select value={form.project_id}
                       onChange={(e) => setForm(f => ({...f, project_id: e.target.value}))}
                       data-testid="customer-rfq-form-project"
                       className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white">
                <option value="">— none —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.project_code ? `${p.project_code} · ` : ''}{p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">
                Contact Person <span className="text-gray-400 font-normal">(Engineer)</span>
              </label>
              <input type="text" value={form.contact_person}
                      onChange={(e) => setForm(f => ({...f, contact_person: e.target.value}))}
                      placeholder="e.g. Eng. Ahmed"
                      data-testid="customer-rfq-form-contact"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            </div>
          </div>

          {/* Lines Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold">Items · <span className="text-gray-500 font-normal">{form.lines.length} line(s)</span></span>
              <button onClick={addLine} data-testid="customer-rfq-form-add-line"
                      className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 font-semibold inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add Row
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60 text-[11px] text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1 w-8">#</th>
                  <th className="text-left px-2 py-1 w-24">Item Ref</th>
                  <th className="text-left px-2 py-1 w-32">Category</th>
                  <th className="text-left px-2 py-1 w-40">Type of Work</th>
                  <th className="text-left px-2 py-1">Description</th>
                  <th className="text-left px-2 py-1 w-32">Size</th>
                  <th className="text-left px-2 py-1 w-24">Unit</th>
                  <th className="text-right px-2 py-1 w-20">Qty</th>
                  <th className="text-left px-2 py-1 w-32">Brand</th>
                  <th className="text-left px-2 py-1 w-28">Brand Mode</th>
                  <th className="px-1 py-1 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((ln, idx) => (
                  <tr key={idx} className="border-t border-gray-100 align-top"
                      data-testid={`customer-rfq-form-line-${idx + 1}`}>
                    <td className="px-2 py-1.5 font-mono text-xs text-gray-500 pt-2">{idx + 1}</td>
                    <td className="px-2 py-1.5">
                      <input value={ln.item_ref || ''}
                              onChange={(e) => updateLine(idx, 'item_ref', e.target.value)}
                              placeholder="auto from category"
                              data-testid={`customer-rfq-form-line-itemref-${idx + 1}`}
                              className="w-full border border-gray-300 rounded px-1 py-1 text-xs font-mono bg-amber-50 placeholder:text-amber-400"
                              title="Auto-fills when you choose a Category — you can override manually" />
                    </td>
                    <td className="px-2 py-1.5">
                      <select value={ln.category || ''}
                               onChange={(e) => updateLine(idx, 'category', e.target.value)}
                               data-testid={`customer-rfq-form-line-category-${idx + 1}`}
                               className="w-full border border-gray-300 rounded px-1 py-1 text-xs font-mono bg-white">
                        <option value="">— optional —</option>
                        {(meta.categories || []).map(c => (
                          <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <select value={ln.type_of_work || ''}
                               onChange={(e) => updateLine(idx, 'type_of_work', e.target.value)}
                               data-testid={`customer-rfq-form-line-type-${idx + 1}`}
                               className="w-full border border-gray-300 rounded px-1 py-1 text-xs bg-white">
                        <option value="">— optional —</option>
                        {(meta.types_of_work || []).map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <textarea rows={5} value={ln.description}
                                 onChange={(e) => updateLine(idx, 'description', e.target.value)}
                                 placeholder="Item description — multi-line (press Enter for new line)"
                                 data-testid={`customer-rfq-form-line-desc-${idx + 1}`}
                                 className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-y leading-relaxed min-h-[120px]" />
                    </td>
                    <td className="px-2 py-1.5">
                      <textarea rows={2} value={ln.size || ''}
                                 onChange={(e) => updateLine(idx, 'size', e.target.value)}
                                 placeholder="dimensions"
                                 data-testid={`customer-rfq-form-line-size-${idx + 1}`}
                                 className="w-full border border-gray-300 rounded px-2 py-1 text-xs resize-y leading-snug" />
                    </td>
                    <td className="px-2 py-1.5">
                      <select value={ln.unit || ''}
                               onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                               data-testid={`customer-rfq-form-line-unit-${idx + 1}`}
                               className="w-full border border-gray-300 rounded px-1 py-1 text-xs font-mono bg-white">
                        <option value="">— optional —</option>
                        {(meta.units || []).map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 text-right pt-1">
                      <input type="number" min={0} value={ln.qty}
                              onChange={(e) => updateLine(idx, 'qty', e.target.value)}
                              data-testid={`customer-rfq-form-line-qty-${idx + 1}`}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-xs text-right" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input value={ln.requested_brand || ''}
                              onChange={(e) => updateLine(idx, 'requested_brand', e.target.value)}
                              placeholder="e.g. Hilti / Sika"
                              data-testid={`customer-rfq-form-line-brand-${idx + 1}`}
                              className="w-full border border-gray-300 rounded px-1 py-1 text-xs" />
                    </td>
                    <td className="px-2 py-1.5">
                      <select value={ln.brand_strictness || ''}
                              onChange={(e) => updateLine(idx, 'brand_strictness', e.target.value)}
                              disabled={!ln.requested_brand}
                              data-testid={`customer-rfq-form-line-strictness-${idx + 1}`}
                              className="w-full border border-gray-300 rounded px-1 py-1 text-xs bg-white disabled:opacity-50">
                        <option value="">—</option>
                        <option value="Specified">Specified</option>
                        <option value="Equivalent">Equivalent Approved</option>
                        <option value="Open">Open</option>
                      </select>
                    </td>
                    <td className="px-1 py-1.5 text-right pt-2">
                      {form.lines.length > 1 && (
                        <button onClick={() => dropLine(idx)}
                                data-testid={`customer-rfq-form-line-drop-${idx + 1}`}
                                className="text-rose-500 hover:text-rose-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Notes (optional)</label>
            <textarea rows={2} value={form.notes}
                       onChange={(e) => setForm(f => ({...f, notes: e.target.value}))}
                       data-testid="customer-rfq-form-notes"
                       className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-y" />
          </div>

          {/* Attachments — BOQ / Specs / Drawing ────────────────── */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold inline-flex items-center gap-2">
              <Paperclip className="w-4 h-4" /> Attachments
              <span className="text-[11px] text-gray-500 font-normal">
                BOQ · Specs · Drawing (up to 10 MB each)
              </span>
            </div>
            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              {[
                { kind: 'boq',     label: 'BOQ',     accept: '.pdf,.xls,.xlsx,.csv,.doc,.docx' },
                { kind: 'specs',   label: 'Specs',   accept: '.pdf,.doc,.docx,.txt' },
                { kind: 'drawing', label: 'Drawing', accept: '.pdf,.dwg,.dxf,.png,.jpg,.jpeg' },
              ].map(({ kind, label, accept }) => {
                const pending = pendingFiles[kind];
                const live    = existingAttachments.filter(a => a.kind === kind);
                return (
                  <div key={kind} className="border border-gray-200 rounded p-2 bg-gray-50/40"
                       data-testid={`customer-rfq-form-attach-${kind}`}>
                    <div className="font-semibold text-gray-800 mb-1.5 inline-flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" /> {label}
                    </div>
                    {/* Saved files (Edit Draft mode) */}
                    {live.map(a => (
                      <div key={a.id}
                            className="flex items-center justify-between bg-white border border-gray-200 rounded px-2 py-1 mb-1"
                            data-testid={`customer-rfq-form-attach-existing-${a.id}`}>
                        <span className="truncate flex-1 mr-1" title={a.filename}>
                          📄 {a.filename}
                          <span className="text-gray-400 ml-1">({Math.round((a.size_bytes || 0) / 1024)} KB)</span>
                        </span>
                        <button onClick={() => removeExisting(a.id)}
                                data-testid={`customer-rfq-form-attach-remove-${a.id}`}
                                className="text-rose-500 hover:text-rose-700">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {/* Pending (New RFQ mode) — shown until save */}
                    {pending && (
                      <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
                        <span className="truncate flex-1 mr-1" title={pending.name}>
                          ⏳ {pending.name}
                          <span className="text-gray-400 ml-1">({Math.round(pending.size / 1024)} KB)</span>
                        </span>
                        <button onClick={() => setPendingFiles(p => ({...p, [kind]: null}))}
                                className="text-rose-500 hover:text-rose-700">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {/* Upload button — direct upload for Edit mode, queued for New mode */}
                    <label className="block">
                      <input type="file" accept={accept} className="hidden"
                              data-testid={`customer-rfq-form-attach-input-${kind}`}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                if (existing) onEditUpload(kind, f);
                                else setPendingFiles(p => ({...p, [kind]: f}));
                                e.target.value = '';
                              }} />
                      <span className="cursor-pointer block text-center px-2 py-1.5 rounded border border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold inline-flex items-center justify-center gap-1 w-full">
                        <Upload className="w-3 h-3" />
                        {(pending || live.length > 0) ? 'Add another' : `Choose ${label} file`}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-between gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="customer-rfq-form-cancel"
                  className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold">
            Cancel
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => submit('draft')} disabled={busy}
                    data-testid="customer-rfq-form-save-draft"
                    className="px-3 py-1.5 rounded border border-amber-400 text-amber-700 hover:bg-amber-50 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
              <Save className="w-4 h-4" /> Save as Draft
            </button>
            <button onClick={() => submit('saved')} disabled={busy}
                    data-testid="customer-rfq-form-save"
                    className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
              <Send className="w-4 h-4" /> Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ─────────────────────── Detail Modal ────────────────────────
const CustomerRFQDetail = ({ rfq, onClose }) => {
  const [categoryMap, setCategoryMap] = useState({});
  useEffect(() => {
    axiosInstance.get('/customer-rfqs/meta').then(r => {
      const map = {};
      (r.data?.categories || []).forEach(c => { map[c.code] = c.label; });
      setCategoryMap(map);
    }).catch(() => {});
  }, []);
  const formatCategory = (code) => {
    if (!code) return '—';
    const label = categoryMap[code];
    return label ? `${code} - ${label}` : code;
  };
  const total = useMemo(
    () => (rfq.lines || []).reduce((s, l) => s + Number(l.qty || 0), 0),
    [rfq],
  );
  const attachmentsByKind = useMemo(() => {
    const out = { boq: [], specs: [], drawing: [] };
    (rfq.attachments || []).forEach(a => { if (out[a.kind]) out[a.kind].push(a); });
    return out;
  }, [rfq]);

  const downloadUrl = (att) =>
    `${axiosInstance.defaults.baseURL}/customer-rfqs/${rfq.id}/attachments/${att.id}/download`;

  const downloadFile = async (att) => {
    try {
      const r = await axiosInstance.get(
        `/customer-rfqs/${rfq.id}/attachments/${att.id}/download`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = att.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (_) { toast.error('Download failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="customer-rfq-detail-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold inline-flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <span className="font-mono text-indigo-700">{rfq.rfq_no}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${STATUS_TONE[rfq.status] || STATUS_TONE.draft}`}>
              {rfq.status}
            </span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <Field label="Date" value={rfq.rfq_date} />
            <Field label="Customer" value={rfq.customer_name} />
            <Field label="Project" value={rfq.project_name} />
            <Field label="Contact Person" value={rfq.contact_person} />
          </div>

          {/* Attachments display ─────────────────────────────── */}
          {(rfq.attachments || []).length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden"
                 data-testid="customer-rfq-detail-attachments">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold inline-flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Attachments
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                {[
                  { kind: 'boq',     label: 'BOQ' },
                  { kind: 'specs',   label: 'Specs' },
                  { kind: 'drawing', label: 'Drawing' },
                ].map(({ kind, label }) => (
                  <div key={kind} className="border border-gray-200 rounded p-2">
                    <p className="font-semibold text-gray-800 mb-1.5">{label}</p>
                    {attachmentsByKind[kind].length === 0 ? (
                      <p className="text-gray-400 italic">— none —</p>
                    ) : (
                      attachmentsByKind[kind].map(att => (
                        <button key={att.id} onClick={() => downloadFile(att)}
                                data-testid={`customer-rfq-detail-download-${att.id}`}
                                className="w-full text-left flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded px-2 py-1 mb-1 hover:bg-indigo-100">
                          <span className="truncate flex-1 mr-1 text-indigo-900" title={att.filename}>
                            📎 {att.filename}
                            <span className="text-indigo-500 ml-1">({Math.round((att.size_bytes || 0) / 1024)} KB)</span>
                          </span>
                          <Download className="w-3 h-3 text-indigo-700 flex-shrink-0" />
                        </button>
                      ))
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Items · <span className="text-gray-500 font-normal">{(rfq.lines || []).length} line(s)</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60 text-[11px] text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1.5 w-8">#</th>
                  <th className="text-left px-2 py-1.5 w-44">Category</th>
                  <th className="text-left px-2 py-1.5 w-40">Type of Work</th>
                  <th className="text-left px-2 py-1.5">Description</th>
                  <th className="text-left px-2 py-1.5 w-32">Size</th>
                  <th className="text-left px-2 py-1.5 w-16">Unit</th>
                  <th className="text-right px-2 py-1.5 w-20">Qty</th>
                </tr>
              </thead>
              <tbody>
                {(rfq.lines || []).map((ln, i) => (
                  <tr key={i} className="border-t border-gray-100 align-top">
                    <td className="px-2 py-1.5 font-mono text-xs text-gray-500">{ln.no}</td>
                    <td className="px-2 py-1.5 text-xs">{formatCategory(ln.category)}</td>
                    <td className="px-2 py-1.5 text-xs">{ln.type_of_work || '—'}</td>
                    <td className="px-2 py-1.5 whitespace-pre-wrap leading-snug">{ln.description}</td>
                    <td className="px-2 py-1.5 whitespace-pre-wrap text-xs">{ln.size || '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{ln.unit || '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-xs text-right">{ln.qty}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td colSpan={6} className="px-2 py-1.5 text-xs font-semibold text-right">Total Qty</td>
                  <td className="px-2 py-1.5 font-mono text-xs font-bold text-right">{total}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {rfq.notes && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Notes</p>
              <p className="text-sm bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap">{rfq.notes}</p>
            </div>
          )}
          <div className="text-[10px] text-gray-500 border-t border-gray-100 pt-2">
            Created by {rfq.created_by_name || '—'} on {(rfq.created_at || '').slice(0, 16).replace('T', ' ')}
            {rfq.updated_at && rfq.updated_at !== rfq.created_at &&
              ` · last updated ${rfq.updated_at.slice(0, 16).replace('T', ' ')}`}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} data-testid="customer-rfq-detail-close"
                  className="px-3 py-1.5 rounded bg-gray-900 text-white text-sm font-semibold">Close</button>
        </div>
      </div>
    </div>
  );
};


const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-sm font-medium">{value || '—'}</p>
  </div>
);


export default CustomerRFQ;
